import StreamManager from '../util/StreamManager'
import StreamWrapper from '../util/StreamWrapper'
import { getLogger } from '../util/log'
import {
    STREAM_TYPE,
    VIDEO_QUALITY_LOW,
    VIDEO_QUALITY_MEDIUM,
    VIDEO_QUALITY_HIGH,
    VIDEO_QUALITY_SUPER,
    VIDEO_QUALITY_720P,
    VIDEO_QUALITY_1080P
} from '../config'
import { stringify } from 'querystring'

const logger = getLogger('brtc stream-manager')
const isChrome = !!navigator.webkitGetUserMedia

// navigator.getDisplayMedia不支持分享音频
const isSupportScreenAudio = !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia)

function getQualityProfile(opts) {
    return {
        width: opts.size.width,
        height: opts.size.height,
        frameRate: opts.fps,
        bitrate: opts.bandwidth || 1000
    }
}

const getDisplayMedia =
    window.navigator.mediaDevices && window.navigator.mediaDevices.getDisplayMedia
        ? window.navigator.mediaDevices.getDisplayMedia.bind(window.navigator.mediaDevices)
        : window.navigator && window.navigator.getDisplayMedia
        ? window.navigator.getDisplayMedia.bind(window.navigator)
        : null

export default class BrtcStreamManager extends StreamManager {
    _createStream(options) {
        return global.BRTC.createStream(options)
    }

    createAVStream(opts) {
        return new Promise((resolve, reject) => {
            if (!opts.type || opts.type === 'noDevice') {
                resolve()
            }
            const options = this.getAVStreamOptions(opts)
            // const stream = global.BRTC.createStream(options)
            let stream
            try {
                stream = this._createStream(options)
            } catch (error) {
                let code = error && error.getCode && error.getCode()
                let name = ''
                if (code && code === '0x1003') {
                    name = 'DevicesNotFoundError'
                }
                reject({
                    name: name,
                    error: error
                })
                return
            }
            // todo: size
            if (options.video) {
                stream.streamCodecOption.bitrate = options.bandwidth
                stream.streamCodecOption.frameRate = options.fps
                stream.setVideoEncoderConfiguration(getQualityProfile(options))
            }

            if (options.audioProfile) {
                stream.setAudioProfile(options.audioProfile)
            }

            stream.init().then(
                () => {
                    resolve(stream)
                },
                (err) => {
                    logger.warn('stream initialize error:', err)
                    // 0x1003
                    if (err && err.code === 4099) {
                        reject(Object.assign({}, err, { name: 'DevicesNotFoundError' }))
                        return
                    }
                    reject(err)
                }
            )
        })
    }

    _createScreenStream(data) {
        let { resolve, reject,videoSource, audioSource, opts } = data
        // 对 videoSource 和 audioSource 进行处理后
        let localStream = global.BRTC.createStream({
            videoSource: videoSource,
            audioSource: audioSource
        })
        logger.info('_createScreenStream', opts, (opts && opts.screenShareProfile) || '720p')
        if (opts && opts.audioProfile) {
            localStream.setAudioProfile(opts.audioProfile)
        }
        if (opts && opts.screenShareProfile) {
            // BRTC 仅支持通过 setVideoEncoderConfiguration 设置屏幕分享的帧率码率，分辨率不可设置
            localStream.setVideoEncoderConfiguration(opts.screenShareProfile) 
        }
        // localStream.setScreenProfile((opts && opts.agoraDefinition) || '720p')
        localStream.init().then(
            () => {
                localStream.sVideoSource = videoSource
                localStream.sAudioSource = audioSource
                resolve(localStream)
            },
            (err) => reject(err)
        )
    }

    createScreenStream(opts) {
        return new Promise((resolve, reject) => {
            if (getDisplayMedia) {
                let videoParams = true
                if (opts && opts.screenShareProfile) {
                    videoParams = {
                        width: {
                            ideal: opts.screenShareProfile.width
                        },
                        height: {
                            ideal: opts.screenShareProfile.height
                        }
                    }
                }
                getDisplayMedia({
                    video: videoParams,
                    audio: opts && (opts.audio || opts.audio === undefined) && isSupportScreenAudio,
                    regionShare: !!opts.regionShare,
                    windowId: opts.windowId,
                    preferCurrentTab: opts.preferCurrentTab
                })
                    .then((mediaStream) => {
                        this._createScreenStream({
                            opts, 
                            videoSource: mediaStream.getVideoTracks()[0], 
                            audioSource: mediaStream.getAudioTracks()[0],
                            resolve,
                            reject
                        })
                    })
                    .catch((err) => reject(err))
            } else if ('getDisplayMedia' in window.navigator) {
                navigator
                    .getDisplayMedia({
                        video: true,
                        audio: opts && (opts.audio || opts.audio === undefined) && isSupportScreenAudio
                    })
                    .then((mediaStream) => {
                        let videoSource = mediaStream.getVideoTracks()[0]
                        // 对 videoSource 和 audioSource 进行处理后
                        this._createScreenStream({
                            resolve,
                            reject,
                            videoSource,
                            opts,
                        })
                    })
                    .catch((err) => reject(err))
            } else if (document.body.dataset.rtcExtensionId != null) {
                window.addEventListener('message', function once(event) {
                    const {
                        data: { type, streamId },
                        origin
                    } = event
                    // NOTE: you should discard foreign events
                    if (origin !== window.location.origin) {
                        logger.warn('ScreenStream: you should discard foreign event from origin:', origin)
                        // return;
                    }
                    // user chose a stream
                    if (type === 'STREAM_SUCCESS') {
                        window.removeEventListener('message', once)
                        navigator.mediaDevices
                            .getUserMedia({
                                audio: false,
                                video: {
                                    mandatory: {
                                        chromeMediaSource: 'desktop',
                                        chromeMediaSourceId: streamId,
                                        maxWidth: window.screen.width,
                                        maxHeight: window.screen.height
                                    }
                                }
                            })
                            .then((mediaStream) => {
                                let videoSource = mediaStream.getVideoTracks()[0]
                                this._createScreenStream({
                                    resolve,
                                    reject,
                                    videoSource,
                                    opts,
                                })
                            })
                    }
                    if (type === 'STREAM_ERROR') {
                        window.removeEventListener('message', once)
                        reject(new Error('permission denied'))
                    }
                })
                window.postMessage({ type: 'STREAM_REQUEST' }, '*')
            } else {
                navigator.mediaDevices
                    .getUserMedia({
                        video: {
                            mandatory: {
                                chromeMediaSource: 'screen',
                                maxWidth: window.screen.width,
                                maxHeight: window.screen.height
                            }
                        }
                    })
                    .then((mediaStream) => {
                        let videoSource = mediaStream.getVideoTracks()[0]
                        this._createScreenStream({
                            resolve,
                            reject,
                            videoSource,
                            opts,
                        })
                    })
                    .catch((e) => {
                        reject(new Error('not supported'))
                    })
            }
        })
    }

    createHTMLMediaStream({ videoElement, fps, quality, bandwidth, profile, audioProfile }) {
        return new Promise((resolve, reject) => {
            if (!videoElement) {
                reject(new Error('videoElement required'))
            }
            const options = this.getHTMLMediaStreamOptions({ videoElement, fps })

            logger.info('createHTMLMediaStream', options)

            const stream = this._createStream(options)
            stream.streamCodecOption.bitrate = bandwidth
            stream.streamCodecOption.frameRate = fps
            let mediaProfile = {
                width: videoElement.videoWidth || 640,
                height: videoElement.videoHeight || 480,
                frameRate: fps || 15,
                bitrate: bandwidth,
                profile: profile || '42e01f'
            }
            stream.setVideoEncoderConfiguration(mediaProfile)
            if (audioProfile) {
                stream.setAudioProfile(audioProfile)
            }
            // todo: size
            stream.init().then(
                () => {
                    // stream.stream = stream.getMediaStream();
                    // stream.stream.getVideoTracks = stream.stream.getVideoTrack;
                    // stream.stream.getAudioTracks = stream.stream.getAudioTrack;
                    resolve(stream)
                },
                (err) => reject(err)
            )
        })
    }

    /**
     * 创建自定义流
     * @param {*} opts
     * @param {MediaStreamTrack} opts.videoTrack
     * @param {MediaStreamTrack} opts.audioTrack
     * @param {number} opts.fps
     * @param {number} opts.bandwidth
     * @param {*} opts.size
     * @param {number} opts.size.width
     * @param {number} opts.size.height
     * @params {string} opts.profile
     */
    createCustomStream(opts) {
        return new Promise((resolve, reject) => {
            const options = this.getCustomStreamOptions(opts)
            const stream = this._createStream(options)
            if (options.videoSource) {
                stream.streamCodecOption.bitrate = opts.bandwidth
                stream.streamCodecOption.frameRate = opts.fps
                stream.setVideoEncoderConfiguration({
                    width: opts.size.width,
                    height: opts.size.height,
                    frameRate: opts.fps,
                    bitrate: opts.bandwidth,
                    profile: opts.profile || '42e01f'
                })
            }
            if (opts && opts.audioProfile) {
                stream.setAudioProfile(opts.audioProfile)
            }
            stream.init().then(
                () => {
                    resolve(stream)
                },
                (err) => reject(err)
            )
        })
    }

    getCustomStreamOptions(opts) {
        const options = {
            fps: opts.fps || this.fps,
            size: opts.size,
            bandwidth: opts.bandwidth
        }
        if (opts.videoTrack) {
            options.videoSource = opts.videoTrack
        }
        if (opts.audioTrack) {
            options.audioSource = opts.audioTrack
        }
        return options
    }

    getAVStreamOptions(opts) {
        const fps = opts.fps || this.fps
        const options = {
            video: false,
            audio: false,
            screen: false,
            fps: fps,
            size: opts.size,
            bandwidth: opts.bandwidth,
            autoGainControl: !!opts.autoGainControl
        }
        if (opts.cameraId) {
            options.cameraId = opts.cameraId
        }
        if (opts.microphoneId) {
            options.microphoneId = opts.microphoneId
        }
        if (opts.noiseSuppression !== undefined) {
            options.noiseSuppression = opts.noiseSuppression
        }
        if (opts.echoCancellation !== undefined) {
            options.echoCancellation = opts.echoCancellation
        }

        if (opts.audioProfile !== undefined) {
            options.audioProfile = opts.audioProfile
        }

        switch (opts.type) {
            case STREAM_TYPE.AV:
                options.video = true
                options.audio = true
                break
            case STREAM_TYPE.VIDEO:
                options.video = true
                break
            case STREAM_TYPE.AUDIO:
                options.audio = true
                break
        }
        return options
    }

    getHTMLMediaStreamOptions(opts) {
        let localStream = null
        // fixme(cc): firefox no audio
        const videoElement = opts.videoElement
        videoElement.captureStream = videoElement.captureStream || videoElement.mozCaptureStream
        if (videoElement.captureStream) {
            const fps = opts.fps || this.fps
            localStream = videoElement.captureStream(fps)
        } else {
            localStream = videoElement
            if (isChrome) {
                logger.warn(
                    'chrome should enable this flag(chrome://flags/#enable-experimental-web-platform-features) for this feature'
                )
            } else {
                logger.warn('the browser not support capturestream feature now')
            }
        }
        const streamWrapper = new StreamWrapper(localStream)
        const videoTrack = streamWrapper.videoTrack
        const audioTrack = streamWrapper.audioTrack

        let options = {}
        if (videoTrack) {
            options.videoSource = videoTrack
        }
        if (audioTrack) {
            options.audioSource = audioTrack
        }
        return options
    }
}
